/**
 * Dates and times for display (AP-7, NFR-9).
 *
 * The database stores UTC; partners read Asia/Dhaka. Every conversion happens
 * here so no screen invents its own.
 *
 * The distinction that matters most in this file is between a *timestamp* and
 * a *calendar date*. `created_at` is an instant and must be converted. A
 * `txn_date` of "2026-08-02" is not an instant at all — it is the day the
 * money moved. Parsing it as UTC midnight and rendering it in Dhaka is
 * harmless (+6 hours stays on the same day), but parsing it in a *negative*
 * offset would show the day before, and a partner reconciling a cash book
 * against dates that are off by one has no way to tell what went wrong.
 * Calendar dates are therefore never passed through a timezone at all.
 */

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone.js';
import utc from 'dayjs/plugin/utc.js';

import { DISPLAY_TIMEZONE } from '@agrofarm/shared';

dayjs.extend(utc);
dayjs.extend(timezone);

/** ISO-8601 instant from the API, e.g. "2026-08-02T20:00:00.000Z". */
export type IsoTimestamp = string;
/** Calendar date from the API, e.g. "2026-08-02". Not an instant. */
export type IsoDate = string;

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** Converts a UTC instant into Dhaka local time. */
export const toDhaka = (timestamp: IsoTimestamp | Date): dayjs.Dayjs => dayjs(timestamp).tz(DISPLAY_TIMEZONE);

/** Today in Dhaka as "YYYY-MM-DD" — the default for a new expense form. */
export const todayInDhaka = (): IsoDate => dayjs().tz(DISPLAY_TIMEZONE).format('YYYY-MM-DD');

/**
 * Formats a calendar date for reading: "2 Aug 2026".
 *
 * Deliberately does not touch timezones. The value is already the day the
 * partner means.
 */
export function formatDate(date: IsoDate): string {
  if (!DATE_ONLY.test(date)) throw new Error(`Expected a YYYY-MM-DD calendar date, got "${date}"`);
  // Parsed without a zone so it can never shift across a day boundary.
  return dayjs(date, 'YYYY-MM-DD').format('D MMM YYYY');
}

/** Formats a UTC instant in Dhaka: "3 Aug 2026, 2:00 am". */
export const formatDateTime = (timestamp: IsoTimestamp | Date): string =>
  toDhaka(timestamp).format('D MMM YYYY, h:mm a');

/** The Dhaka calendar day an instant falls on — what "today's expenses" means. */
export const dhakaDateOf = (timestamp: IsoTimestamp | Date): IsoDate => toDhaka(timestamp).format('YYYY-MM-DD');

/** The API wants YYYY-MM-DD; a date input gives one back directly (§7). */
export function toApiDate(date: Date | dayjs.Dayjs | IsoDate): IsoDate {
  if (typeof date === 'string') {
    if (!DATE_ONLY.test(date)) throw new Error(`Expected a YYYY-MM-DD calendar date, got "${date}"`);
    return date;
  }
  return dayjs(date).tz(DISPLAY_TIMEZONE).format('YYYY-MM-DD');
}

/**
 * Whole days between two calendar dates, for receivables aging (BR-24) and
 * reminder lead times (P2-BR6).
 */
export function daysBetween(from: IsoDate, to: IsoDate): number {
  return dayjs(to, 'YYYY-MM-DD').diff(dayjs(from, 'YYYY-MM-DD'), 'day');
}

/** Days since a calendar date, counted against today in Dhaka. */
export const daysAgo = (date: IsoDate): number => daysBetween(date, todayInDhaka());

/**
 * A short relative label for lists: "today", "yesterday", "3 days ago".
 *
 * Returned as an i18n key plus its count so the caller translates it —
 * hardcoding English here would violate the no-hardcoded-strings rule.
 */
export function relativeDayKey(date: IsoDate): {
  key: 'date.today' | 'date.yesterday' | 'date.daysAgo';
  count: number;
} {
  const days = daysAgo(date);
  if (days <= 0) return { key: 'date.today', count: 0 };
  if (days === 1) return { key: 'date.yesterday', count: 1 };
  return { key: 'date.daysAgo', count: days };
}
